import type { SocialPostPlatform, SocialPostType } from "@runcheetah/shared";

export type SocialPostChromeVariant = "instagram_feed" | "instagram_story";

export type SocialPostChromeFrame = {
  width: number;
  height: number;
  mediaX: number;
  mediaY: number;
  mediaWidth: number;
  mediaHeight: number;
};

const FEED_HORIZONTAL_CHROME = 96;
const FEED_HEADER_HEIGHT = 84;
const FEED_ACTIONS_HEIGHT = 64;
const FEED_CAPTION_HEIGHT = 260;
const STORY_HORIZONTAL_CHROME = 360;
const STORY_VERTICAL_CHROME = 80;

export const resolveSocialPostChromeVariant = (
  platform: SocialPostPlatform,
  postType: SocialPostType,
): SocialPostChromeVariant => {
  if ((platform === "instagram" && postType === "story") || platform === "tiktok") {
    return "instagram_story";
  }

  return "instagram_feed";
};

export const resolveSocialPostChromeFrame = (input: {
  variant: SocialPostChromeVariant;
  mediaWidth: number;
  mediaHeight: number;
}): SocialPostChromeFrame => {
  const mediaWidth = Math.max(1, Math.round(input.mediaWidth));
  const mediaHeight = Math.max(1, Math.round(input.mediaHeight));

  if (input.variant === "instagram_story") {
    return {
      width: mediaWidth + STORY_HORIZONTAL_CHROME,
      height: mediaHeight + STORY_VERTICAL_CHROME,
      mediaX: 40,
      mediaY: 40,
      mediaWidth,
      mediaHeight,
    };
  }

  return {
    width: mediaWidth + FEED_HORIZONTAL_CHROME,
    height: mediaHeight + FEED_HEADER_HEIGHT + FEED_ACTIONS_HEIGHT + FEED_CAPTION_HEIGHT,
    mediaX: FEED_HORIZONTAL_CHROME / 2,
    mediaY: FEED_HEADER_HEIGHT,
    mediaWidth,
    mediaHeight,
  };
};

export const formatInstagramProfileLabel = (sourceDomain: string | null | undefined): string => {
  const compact =
    sourceDomain
      ?.trim()
      .toLowerCase()
      .replace(/^www\./, "") ?? "";
  const handle = compact.split(".").filter(Boolean)[0] ?? "brand";

  return handle.replace(/[^a-z0-9._]+/g, "").slice(0, 30) || "brand";
};

export const normalizeHashtagToken = (value: string): string | null => {
  const token = value.trim().replace(/^#+/, "").replace(/\s+/g, "");

  return token.length > 0 ? token : null;
};

export const formatHashtagToken = (value: string): string => {
  const token = normalizeHashtagToken(value);

  return token ? `#${token}` : "";
};

export const addHashtagToken = (tokens: string[], value: string): string[] => {
  const token = normalizeHashtagToken(value);

  return token ? [...tokens, token] : tokens;
};

export const updateHashtagToken = (tokens: string[], index: number, value: string): string[] => {
  if (index < 0 || index >= tokens.length) {
    return tokens;
  }

  const token = normalizeHashtagToken(value);
  if (!token) {
    return tokens.toSpliced(index, 1);
  }

  return tokens.with(index, token);
};

export const removeHashtagToken = (tokens: string[], index: number): string[] => {
  if (index < 0 || index >= tokens.length) {
    return tokens;
  }

  return tokens.toSpliced(index, 1);
};
